// Flow by point: the traffic on the nearest road to a coordinate, by hour and direction.
// POST /data-agg with agg_type 'flow' and a lat/lon, then poll (see dataJobs.ts).
import type { CountryCode } from '../config/countries';
import type { TimeWindow } from '../utils/dateWindow';
import { bucketToIsoUtc } from '../utils/dateWindow';
import { runJob } from './dataJobs';
import type { RunJobOptions } from './dataJobs';

export interface FlowPointRequest {
  country: CountryCode;
  lat: number;
  lon: number;
  window: TimeWindow;
}

export interface FlowRow {
  link_id: string | null;
  link_name: string | null;
  link_type: string | null;
  distance_m: number | null;
  /** ISO UTC (see bucketToIsoUtc); handles {value} wrappers and 'YYYY-MM-DD HH:MM:SS'. */
  time_bucket: string | null;
  direction: number | null;
  people: number | null;
}

/** Six decimal places is about 10 cm: enough for a road lookup, and keeps the body stable. */
export const roundCoord = (x: number) => Math.round(x * 1e6) / 1e6;

export function flowRequestBody(r: FlowPointRequest): {
  agg_type: 'flow';
  country_code: CountryCode;
  lat: number;
  lon: number;
  start_time: string;
  end_time: string;
} {
  return {
    agg_type: 'flow',
    country_code: r.country,
    lat: roundCoord(r.lat),
    lon: roundCoord(r.lon),
    start_time: r.window.start_time,
    end_time: r.window.end_time,
  };
}

function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Rows as the API sends them (INT64 may arrive as strings) to typed rows. Non-objects are dropped. */
export function normalizeFlowRows(raw: readonly unknown[]): FlowRow[] {
  const out: FlowRow[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const id = r.link_id;
    out.push({
      link_id: id === null || id === undefined || id === '' ? null : String(id),
      link_name: text(r.link_name),
      link_type: text(r.link_type),
      distance_m: num(r.distance_m),
      time_bucket: bucketToIsoUtc(r.time_bucket),
      direction: num(r.direction),
      people: num(r.people),
    });
  }
  return out;
}

export async function fetchFlowAtPoint(r: FlowPointRequest, o?: RunJobOptions): Promise<FlowRow[]> {
  const { rows } = await runJob<unknown>(flowRequestBody(r), o);
  return normalizeFlowRows(rows);
}
